import { z } from 'zod';
import type { PriceTypeToken } from '@sleed/bizcommerce-api';
import { PriceTaxMode } from '@sleed/bizcommerce-api';

/**
 * Price type edit form. Mirrors `PriceTypeToken` and the legacy admin grid
 * (Core/client/admin/src/pages/Pricing/PriceTypes/index.js): nm required ≤100, base/tax/iv
 * booleans, si required. `taxMode` and `cd` are not surfaced by the legacy grid; they are
 * carried through unchanged so edits round-trip the full token (parity with TaxOffices' tel/erpcd).
 */
export const priceTypeSchema = z.object({
	id: z.number().optional(),
	nm: z.string().trim().min(1, 'validation.required').max(100, 'validation.tooLong'),
	base: z.boolean(),
	tax: z.boolean(),
	iv: z.boolean(),
	si: z.number({ message: 'validation.required' }).int(),
	taxMode: z.nativeEnum(PriceTaxMode),
	cd: z.string().nullable()
});

export type PriceTypeFormValues = z.infer<typeof priceTypeSchema>;

export const EMPTY_PRICE_TYPE: PriceTypeFormValues = {
	nm: '',
	base: false,
	tax: true,
	iv: true,
	si: 0,
	taxMode: PriceTaxMode.BeforeTaxes,
	cd: null
};

export function tokenToForm(token: PriceTypeToken): PriceTypeFormValues {
	return {
		id: token.id,
		nm: token.nm ?? '',
		base: token.base ?? false,
		tax: token.tax ?? false,
		iv: token.iv ?? false,
		si: token.si ?? 0,
		taxMode: token.taxMode ?? PriceTaxMode.BeforeTaxes,
		cd: token.cd ?? null
	};
}

export function formToToken(form: PriceTypeFormValues): PriceTypeToken {
	return {
		id: form.id,
		nm: form.nm.trim(),
		base: form.base,
		tax: form.tax,
		iv: form.iv,
		si: form.si,
		taxMode: form.taxMode,
		cd: form.cd
	};
}
